import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import DisputeThread, { StatusBadge } from "../components/DisputeThread";
import AutopilotToggle from "../components/AutopilotToggle";
import AutopilotDigest from "../components/AutopilotDigest";
import DepositQueue from "../components/DepositQueue";
import SupportInbox from "../components/SupportInbox";
import DriverReviewQueue from "../components/DriverReviewQueue";
import GigDisputeQueue from "../components/GigDisputeQueue";
import PromoAdmin from "../components/PromoAdmin";
import BillingAdmin from "../components/BillingAdmin";
import DriverCardWaitlistAdmin from "../components/DriverCardWaitlistAdmin";
import StaffAdmin from "../components/StaffAdmin";
import GrowthDashboard from "../components/GrowthDashboard";
import { useSearchParams } from "react-router-dom";
import { ShieldAlert, PauseCircle, PlayCircle, DollarSign, Users, CalendarCheck, Loader2, Gavel, Lock, Bot, Crown, Headset, Truck, Users as UsersIcon, TrendingUp, HelpCircle, ShoppingBag, Building2, Wrench, Inbox , Film, Briefcase, Compass, Plug } from "lucide-react";
import FaqAdmin from "../components/FaqAdmin";
import ShopAdmin from "../components/ShopAdmin";
import InvestorAdmin from "../components/InvestorAdmin";
import DataRoomAdmin from "../components/DataRoomAdmin";
import FundingRequestsAdmin from "../components/FundingRequestsAdmin";
import TransportAdmin from "../components/TransportAdmin";
import RevenueReport from "../components/RevenueReport";
import FranchiseAdmin from "../components/FranchiseAdmin";
import HiringAdmin from "../components/HiringAdmin";
import DealersAdmin from "../components/DealersAdmin";
import GrowthEngine from "../components/GrowthEngine";
import AutoCareInbox from "../components/AutoCareInbox";
import DemoResetPanel from "../components/DemoResetPanel";
import DigestAdminPanel from "../components/DigestAdminPanel";
import DigestSummaryCard from "../components/DigestSummaryCard";
import TestimonialsAdmin from "../components/TestimonialsAdmin";
import ChecklistRewardsAdmin from "../components/ChecklistRewardsAdmin";
import HostLeadsAdmin from "../components/HostLeadsAdmin";
import AdminCreditGrants from "../components/AdminCreditGrants";
import { GrowthReportCard, DripSubjectsAdmin, GiftCardSalesAdmin, SlackWebhookAdmin, WeeklyWinsCard } from "../components/GrowthAdminExtras";
import CastAppAdmin from "../components/CastAppAdmin";
import ServicesQueue from "../components/ServicesQueue";
import GigLifeDesk from "../components/GigLifeDesk";
import ReiDesk from "../components/ReiDesk";
import VloAdmin from "../components/VloAdmin";
import ChannelHub from "../components/ChannelHub";
import GlPartnersAdmin from "../components/GlPartnersAdmin";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

function Stat({ icon: I, label, value, tid }) {
  return (
    <div className="p-4 rounded-2xl bg-[#131722] border border-slate-800" data-testid={tid}>
      <div className="flex items-center gap-2 text-slate-400 text-xs font-mono uppercase tracking-widest"><I className="w-3.5 h-3.5" />{label}</div>
      <p className="font-display font-extrabold text-2xl mt-1">{value}</p>
    </div>
  );
}

function Actions({ detail, reload }) {
  const { dispute: d, payout, owner, refundable, has_stripe_payment } = detail;
  const [status, setStatus] = useState(d.status);
  const [note, setNote] = useState("");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState(has_stripe_payment ? "stripe" : "credit");
  const [busy, setBusy] = useState("");

  const run = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); toast.success(ok); await reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(""); }
  };

  const payoutPaused = payout?.status === "paused";
  return (
    <div className="space-y-4" data-testid="admin-actions">
      <div className="p-4 rounded-xl bg-slate-900/60 border border-slate-800 space-y-3">
        <p className="text-xs font-mono uppercase tracking-widest text-cyan-400 flex items-center gap-1.5"><Gavel className="w-3.5 h-3.5" />Status</p>
        <div className="flex gap-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="admin-status-select" className="bg-slate-900 border-slate-800 flex-1"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#131722] border-slate-800">
              {["open", "under_review", "resolved", "rejected"].map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" disabled={busy === "status" || status === d.status} data-testid="admin-status-save"
            onClick={() => run("status", () => api.post(`/admin/disputes/${d.dispute_id}/status`, { status, note: note || null }), "Status updated")}
            className="bg-white text-slate-950 hover:bg-slate-100 font-semibold">{busy === "status" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update"}</Button>
        </div>
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note to both parties (optional)" data-testid="admin-status-note" className="bg-slate-900 border-slate-800" />
      </div>

      <div className="p-4 rounded-xl bg-slate-900/60 border border-slate-800 space-y-3">
        <p className="text-xs font-mono uppercase tracking-widest text-amber-400 flex items-center gap-1.5"><PauseCircle className="w-3.5 h-3.5" />Payouts</p>
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-300">Booking payout {payout ? <span className="font-mono text-slate-400">${payout.amount?.toFixed(2)} · {payout.status}</span> : <span className="text-slate-500">none yet</span>}</span>
          {payout && payout.status !== "paid" && (
            <Button size="sm" variant="outline" disabled={busy === "payout"} data-testid="admin-toggle-booking-payout"
              onClick={() => run("payout", () => api.post("/admin/payouts/pause", { booking_id: d.booking_id, paused: !payoutPaused }), payoutPaused ? "Payout resumed" : "Payout paused")}
              className={`bg-transparent border-slate-700 ${payoutPaused ? "text-emerald-300" : "text-amber-300"}`}>
              {payoutPaused ? <><PlayCircle className="w-3.5 h-3.5 mr-1" />Resume</> : <><PauseCircle className="w-3.5 h-3.5 mr-1" />Pause</>}
            </Button>
          )}
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-300">All payouts for host <span className="text-slate-400">{owner?.name}</span> {owner?.payouts_paused && <span className="text-amber-400 font-mono text-xs">PAUSED</span>}</span>
          <Button size="sm" variant="outline" disabled={busy === "owner"} data-testid="admin-toggle-owner-payouts"
            onClick={() => run("owner", () => api.post("/admin/payouts/pause", { owner_id: d.owner_id, paused: !owner?.payouts_paused }), owner?.payouts_paused ? "Host payouts resumed" : "Host payouts paused")}
            className={`bg-transparent border-slate-700 ${owner?.payouts_paused ? "text-emerald-300" : "text-amber-300"}`}>
            {owner?.payouts_paused ? <><PlayCircle className="w-3.5 h-3.5 mr-1" />Resume all</> : <><PauseCircle className="w-3.5 h-3.5 mr-1" />Pause all</>}
          </Button>
        </div>
      </div>

      <div className="p-4 rounded-xl bg-slate-900/60 border border-slate-800 space-y-3">
        <p className="text-xs font-mono uppercase tracking-widest text-emerald-400 flex items-center gap-1.5"><DollarSign className="w-3.5 h-3.5" />Partial refund <span className="text-slate-500 normal-case tracking-normal ml-auto">refundable ${refundable?.toFixed(2)}</span></p>
        <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
          <div><Label className="text-xs">Amount (USD)</Label><Input type="number" step="0.01" min={0.01} max={refundable} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="admin-refund-amount" className="bg-slate-900 border-slate-800 mt-1" /></div>
          <div><Label className="text-xs">Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="admin-refund-method" className="bg-slate-900 border-slate-800 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#131722] border-slate-800">
                <SelectItem value="stripe" disabled={!has_stripe_payment}>Stripe refund</SelectItem>
                <SelectItem value="credit">Platform credit</SelectItem>
              </SelectContent>
            </Select></div>
          <Button size="sm" disabled={busy === "refund" || !amount || Number(amount) <= 0 || Number(amount) > refundable} data-testid="admin-refund-submit"
            onClick={() => run("refund", () => api.post(`/admin/disputes/${d.dispute_id}/refund`, { amount: Number(amount), method, note: note || null }).then(() => setAmount("")), "Refund issued")}
            className="h-10 bg-emerald-500 text-slate-950 hover:bg-emerald-400 font-semibold">{busy === "refund" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refund"}</Button>
        </div>
        {!has_stripe_payment && <p className="text-[11px] text-slate-500">No captured Stripe payment on this booking — only platform credit is available.</p>}
        {d.refunds?.length > 0 && <p className="text-xs text-slate-400 font-mono" data-testid="admin-refund-history">Refunded so far: ${d.refunds.reduce((s, r) => s + r.amount, 0).toFixed(2)}</p>}
      </div>
    </div>
  );
}

export default function AdminQueue() {
  const { user, loading } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "disputes";
  const [overview, setOverview] = useState(null);
  const [filter, setFilter] = useState("active");
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  const loadList = useCallback(async () => {
    const [o, l] = await Promise.all([api.get("/admin/overview"), api.get(`/admin/disputes?status=${filter === "all" ? "" : filter}`)]);
    setOverview(o.data); setList(l.data);
  }, [filter]);
  const loadDetail = useCallback(async () => {
    if (!selected) return;
    const { data } = await api.get(`/admin/disputes/${selected}`);
    data.dispute._admin = true;
    setDetail(data);
  }, [selected]);

  useEffect(() => { if (user?.is_admin) loadList(); }, [user, loadList]);
  useEffect(() => { loadDetail(); }, [loadDetail]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (!user?.is_admin) return <Navigate to="/dashboard" replace />;

  const reloadAll = async () => { await Promise.all([loadList(), loadDetail()]); };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10" data-testid="admin-queue-page">
      <div className="mb-8 rise">
        <p className="font-mono text-xs tracking-widest uppercase text-cyan-400 mb-1">// Operator console</p>
        <h1 className="font-display text-4xl font-extrabold tracking-tight flex items-center gap-3"><ShieldAlert className="w-8 h-8 text-amber-400" />Admin Review Queue</h1>
      </div>

      <div className="mb-6 rise rise-1"><AutopilotToggle /></div>
      <DigestSummaryCard />

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-8 rise rise-1">
        <Stat icon={ShieldAlert} label="Open disputes" value={overview?.open_disputes ?? "–"} tid="stat-open-disputes" />
        <Stat icon={PauseCircle} label="Paused payouts" value={overview?.paused_payouts ?? "–"} tid="stat-paused-payouts" />
        <Stat icon={Lock} label="Held deposits" value={overview?.held_deposits ?? "–"} tid="stat-held-deposits" />
        <Stat icon={DollarSign} label="Refunded" value={`$${(overview?.refunded_total ?? 0).toFixed(2)}`} tid="stat-refunded" />
        <Stat icon={Users} label="Users" value={overview?.users ?? "–"} tid="stat-users" />
        <Stat icon={CalendarCheck} label="Bookings" value={overview?.bookings ?? "–"} tid="stat-bookings" />
      </div>

      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })} className="mb-6 rise rise-2">
        <TabsList className="bg-slate-900/60 border border-slate-800">
          <TabsTrigger value="disputes" data-testid="admin-tab-disputes"><Gavel className="w-3.5 h-3.5 mr-1.5" />Disputes</TabsTrigger>
          <TabsTrigger value="deposits" data-testid="admin-tab-deposits"><Lock className="w-3.5 h-3.5 mr-1.5" />Deposits</TabsTrigger>
          <TabsTrigger value="digest" data-testid="admin-tab-digest"><Bot className="w-3.5 h-3.5 mr-1.5" />AI Digest</TabsTrigger>
          <TabsTrigger value="services" data-testid="admin-tab-services"><Inbox className="w-3.5 h-3.5 mr-1.5" />Services</TabsTrigger>
          <TabsTrigger value="support" data-testid="admin-tab-support"><Headset className="w-3.5 h-3.5 mr-1.5" />Support Inbox</TabsTrigger>
          <TabsTrigger value="drivers" data-testid="admin-tab-drivers"><Truck className="w-3.5 h-3.5 mr-1.5" />Drivers</TabsTrigger>
          <TabsTrigger value="growth" data-testid="admin-tab-growth"><TrendingUp className="w-3.5 h-3.5 mr-1.5" />Growth</TabsTrigger>
          <TabsTrigger value="staff" data-testid="admin-tab-staff"><UsersIcon className="w-3.5 h-3.5 mr-1.5" />Staff</TabsTrigger>
          <TabsTrigger value="faq" data-testid="admin-tab-faq"><HelpCircle className="w-3.5 h-3.5 mr-1.5" />FAQ</TabsTrigger>
          <TabsTrigger value="shop" data-testid="admin-tab-shop"><ShoppingBag className="w-3.5 h-3.5 mr-1.5" />Shop</TabsTrigger>
          <TabsTrigger value="investors" data-testid="admin-tab-investors"><Lock className="w-3.5 h-3.5 mr-1.5" />Investors</TabsTrigger>
          <TabsTrigger value="transport" data-testid="admin-tab-transport"><Truck className="w-3.5 h-3.5 mr-1.5" />Transport</TabsTrigger>
          <TabsTrigger value="revenue" data-testid="admin-tab-revenue"><TrendingUp className="w-3.5 h-3.5 mr-1.5" />Revenue</TabsTrigger>
          <TabsTrigger value="franchise" data-testid="admin-tab-franchise"><Building2 className="w-3.5 h-3.5 mr-1.5" />Franchise</TabsTrigger>
          <TabsTrigger value="hiring" data-testid="admin-tab-hiring"><Briefcase className="w-3.5 h-3.5 mr-1.5" />Hiring</TabsTrigger>
          <TabsTrigger value="dealers" data-testid="admin-tab-dealers"><Building2 className="w-3.5 h-3.5 mr-1.5" />Dealers</TabsTrigger>
          <TabsTrigger value="rei" data-testid="admin-tab-rei"><Building2 className="w-3.5 h-3.5 mr-1.5" />REI</TabsTrigger>
          <TabsTrigger value="vlo" data-testid="admin-tab-vlo"><Compass className="w-3.5 h-3.5 mr-1.5" />VLO</TabsTrigger>
          <TabsTrigger value="channels" data-testid="admin-tab-channels"><Plug className="w-3.5 h-3.5 mr-1.5" />Channels</TabsTrigger>
          <TabsTrigger value="partners" data-testid="admin-tab-partners"><Film className="w-3.5 h-3.5 mr-1.5" />PARTNERS</TabsTrigger>
          <TabsTrigger value="giglife" data-testid="admin-tab-giglife"><Film className="w-3.5 h-3.5 mr-1.5" />GIG LIFE</TabsTrigger>
          <TabsTrigger value="engine" data-testid="admin-tab-engine"><TrendingUp className="w-3.5 h-3.5 mr-1.5" />Growth Engine</TabsTrigger>
          <TabsTrigger value="autocare" data-testid="admin-tab-autocare"><Wrench className="w-3.5 h-3.5 mr-1.5" />Auto Care</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "faq" && <FaqAdmin />}
      {tab === "shop" && <ShopAdmin />}
      {tab === "investors" && <div className="space-y-10"><InvestorAdmin /><FundingRequestsAdmin /><DataRoomAdmin /></div>}
      {tab === "transport" && <TransportAdmin />}
      {tab === "revenue" && <RevenueReport />}
      {tab === "franchise" && <FranchiseAdmin />}
      {tab === "hiring" && <HiringAdmin />}
      {tab === "dealers" && <DealersAdmin />}
      {tab === "engine" && <GrowthEngine />}
      {tab === "autocare" && <AutoCareInbox />}
      {tab === "growth" && <div className="space-y-8"><GrowthDashboard /><GrowthReportCard /><SlackWebhookAdmin /><WeeklyWinsCard /><CastAppAdmin /><DripSubjectsAdmin /><AdminCreditGrants /><GiftCardSalesAdmin /><TestimonialsAdmin /><ChecklistRewardsAdmin /><HostLeadsAdmin /></div>}
      {tab === "staff" && <StaffAdmin />}
      {tab === "support" && <SupportInbox />}
      {tab === "services" && <ServicesQueue />}
      {tab === "drivers" && <div className="space-y-8"><DemoResetPanel /><DigestAdminPanel /><DriverReviewQueue /><GigDisputeQueue /><BillingAdmin /><PromoAdmin /><DriverCardWaitlistAdmin /></div>}

      {tab === "deposits" && <DepositQueue onChanged={() => loadList()} />}
      {tab === "digest" && <AutopilotDigest />}
      {tab === "giglife" && <GigLifeDesk />}
      {tab === "rei" && <ReiDesk />}
      {tab === "vlo" && <VloAdmin />}
      {tab === "channels" && <ChannelHub />}
      {tab === "partners" && <GlPartnersAdmin />}
      {tab === "disputes" && <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6 rise rise-2">
        <section>
          <Tabs value={filter} onValueChange={(v) => { setFilter(v); setSelected(null); setDetail(null); }}>
            <TabsList className="bg-slate-900/60 border border-slate-800 w-full">
              {["active", "resolved", "rejected", "all"].map((f) => <TabsTrigger key={f} value={f} data-testid={`filter-${f}`} className="flex-1 capitalize">{f}</TabsTrigger>)}
            </TabsList>
          </Tabs>
          <div className="mt-3 space-y-2 max-h-[70vh] overflow-y-auto pr-1" data-testid="admin-dispute-list">
            {list.length === 0 && <p className="text-sm text-slate-500 p-6 text-center border border-dashed border-slate-800 rounded-xl">Nothing in this queue.</p>}
            {list.map((d) => (
              <button key={d.dispute_id} onClick={() => setSelected(d.dispute_id)} data-testid={`admin-dispute-${d.dispute_id}`}
                className={`w-full text-left p-4 rounded-xl border transition-colors ${selected === d.dispute_id ? "bg-[#161b28] border-cyan-500/50" : "bg-[#131722] border-slate-800 hover:border-slate-600"}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="font-semibold truncate">{d.car_label}</p><span className="flex items-center gap-1.5">{d.priority && <Badge data-testid={`priority-badge-${d.dispute_id}`} className="bg-amber-500/20 text-amber-300 border border-amber-500/30 text-[10px] uppercase"><Crown className="w-3 h-3 mr-1" />Pro</Badge>}<StatusBadge status={d.status} /></span>
                </div>
                <p className="text-xs text-slate-400 mt-1 truncate">{d.kind.replace(/_/g, " ")} · by {d.opened_by_name} ({d.opened_by_role})</p>
                <p className="text-[11px] text-slate-500 font-mono mt-1">{new Date(d.created_at).toLocaleString()} · {(d.events || []).length} updates</p>
              </button>
            ))}
          </div>
        </section>

        <section className="min-h-[400px]">
          {!detail ? (
            <div className="h-full min-h-[400px] flex items-center justify-center rounded-2xl border border-dashed border-slate-800 text-slate-500 text-sm" data-testid="admin-empty-detail">Select a dispute to triage</div>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="p-4 rounded-xl bg-[#131722] border border-slate-800 text-sm grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 min-w-0" data-testid="admin-booking-summary">
                  <span className="text-slate-500">Booking</span><span className="font-mono text-xs truncate">{detail.dispute.booking_id}</span>
                  <span className="text-slate-500">Dates</span><span>{detail.booking ? `${detail.booking.start_date} → ${detail.booking.end_date}` : <span className="text-slate-500">booking not found</span>}</span>
                  <span className="text-slate-500">Total paid</span><span className="font-mono">{detail.booking ? `$${(detail.booking.total ?? 0).toFixed(2)} · ${detail.booking.payment_status || "unpaid"}` : "—"}</span>
                  <span className="text-slate-500">Host</span><span className="truncate min-w-0">{detail.owner?.name} <span className="text-slate-500 text-xs">{detail.owner?.email}</span></span>
                  <span className="text-slate-500">Driver</span><span className="truncate min-w-0">{detail.renter?.name} <span className="text-slate-500 text-xs">{detail.renter?.email}</span></span>
                </div>
                <DisputeThread dispute={detail.dispute} onUpdated={(d) => setDetail((x) => ({ ...x, dispute: { ...d, _admin: true } }))} />
              </div>
              <Actions key={detail.dispute.dispute_id + detail.dispute.status + (detail.payout?.status || "") + String(detail.owner?.payouts_paused)} detail={detail} reload={reloadAll} />
            </div>
          )}
        </section>
      </div>}
    </main>
  );
}
